import React from 'react';

/* ── StatusBadge ─────────────────────────────────────────────────────────────
   Renders a styled pill badge for any status string.
   Covers: active, inactive, suspended, pending, verified, unverified,
           scheduled, completed, cancelled, placed, approved,
           out-for-delivery, delivered, failed, refunded, in-stock,
           low-stock, out-of-stock
*/
const STATUS_STYLES = {
  // Doctor / Patient
  active:           'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 ring-1 ring-green-200 dark:ring-green-800',
  inactive:         'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-800',
  suspended:        'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-800',
  pending:          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 ring-1 ring-yellow-200 dark:ring-yellow-800',
  // Verification
  verified:         'bg-medihub-light text-medihub-secondary dark:bg-medihub-dark/40 dark:text-medihub-highlight ring-1 ring-medihub-soft dark:ring-medihub-dark',
  unverified:       'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-600',
  // Appointments
  scheduled:        'bg-medihub-light text-medihub-secondary dark:bg-medihub-dark/40 dark:text-medihub-highlight ring-1 ring-medihub-soft dark:ring-medihub-dark',
  completed:        'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 ring-1 ring-green-200 dark:ring-green-800',
  cancelled:        'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-800',
  confirmed:        'bg-medihub-light text-medihub-secondary dark:bg-medihub-dark/40 dark:text-medihub-highlight ring-1 ring-medihub-soft dark:ring-medihub-dark',
  // Orders
  placed:           'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-800',
  approved:         'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 ring-1 ring-indigo-200 dark:ring-indigo-800',
  assigned:         'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-800',
  'out-for-delivery':'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 ring-1 ring-orange-200 dark:ring-orange-800',
  delivered:        'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 ring-1 ring-green-200 dark:ring-green-800',
  // Payments
  failed:           'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-800',
  refunded:         'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 ring-1 ring-purple-200 dark:ring-purple-800',
  // Inventory
  'in-stock':       'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 ring-1 ring-green-200 dark:ring-green-800',
  'low-stock':      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 ring-1 ring-yellow-200 dark:ring-yellow-800',
  'out-of-stock':   'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-800',
};

const STATUS_DOTS = {
  active: 'bg-green-500',
  inactive: 'bg-red-500',
  suspended: 'bg-red-500',
  pending: 'bg-yellow-500',
  verified:  'bg-medihub-primary',
  unverified: 'bg-gray-400',
  scheduled:  'bg-medihub-primary',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500',
  placed: 'bg-sky-500',
  approved: 'bg-indigo-500',
  assigned: 'bg-violet-500',
  'out-for-delivery': 'bg-orange-500',
  delivered: 'bg-green-500',
  failed: 'bg-red-500',
  refunded: 'bg-purple-500',
};

const LABELS = {
  'out-for-delivery': 'Out for Delivery',
  'in-stock': 'In Stock',
  'low-stock': 'Low Stock',
  'out-of-stock': 'Out of Stock',
};

export const StatusBadge = ({ status, dot = true }) => {
  if (!status) return null;
  const style = STATUS_STYLES[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-600';
  const dotColor = STATUS_DOTS[status] || 'bg-gray-400';
  const label = LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${style}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />}
      {label}
    </span>
  );
};

/* ── VerifiedBadge ───────────────────────────────────────────────────────────
   Dedicated badge for doctor verification state
*/
export const VerifiedBadge = ({ verified }) => (
  <StatusBadge status={verified ? 'verified' : 'unverified'} />
);

/* ── ToggleSwitch ────────────────────────────────────────────────────────────
   Accessible toggle switch with label
   Props: checked, onChange, label, size ('sm'|'md')
*/
export const ToggleSwitch = ({ checked, onChange, label, size = 'md', disabled = false }) => {
  const track = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11';
  const thumb = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const translate = size === 'sm' ? 'translate-x-4' : 'translate-x-6';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-medihub-primary ${track} ${checked ? 'bg-medihub-primary' : 'bg-gray-300 dark:bg-gray-600'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      title={label}
    >
      <span
        className={`inline-block rounded-full bg-white shadow-sm transition-transform ${thumb} ${checked ? translate : 'translate-x-1'}`}
      />
    </button>
  );
};

/* ── StatusToggleCell ────────────────────────────────────────────────────────
   Table cell combining badge + toggle switch
*/
export const StatusToggleCell = ({ active, onToggle, activeLabel = 'Active', inactiveLabel = 'Inactive' }) => (
  <div className="flex items-center gap-2">
    <ToggleSwitch checked={active} onChange={onToggle} label={active ? activeLabel : inactiveLabel} size="sm" />
    <StatusBadge status={active ? 'active' : 'inactive'} dot={false} />
  </div>
);

export const VerifiedToggleCell = ({ verified, onToggle }) => (
  <div className="flex items-center gap-2">
    <ToggleSwitch checked={verified} onChange={onToggle} label={verified ? 'Verified' : 'Unverified'} size="sm" />
    <VerifiedBadge verified={verified} />
  </div>
);

export default StatusBadge;
